import { useEffect, useCallback, MutableRefObject, useRef } from 'react';
import { useRoute, useNavigation, useFocusEffect } from '@react-navigation/native';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { formatStartOf, localTime } from '@helpers';
import { setSelectedChartDate, setSelectedChartDateEnd } from '@reducers/machinesV2';
import { setTimePeriod } from '@reducers/params';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useTimelineSelectors } from './useTimelineSelectors';
import { AbortControllerRef } from '@interfaces/timeline';

interface UseManageTimelineEffectsParams {
  // Refs for cleanup
  abortControllerEvents?: AbortControllerRef;
  abortControllerProductionBatches?: AbortControllerRef;
  abortControllerAlarmEvent?: AbortControllerRef;
  abortControllerOperationsSearch?: AbortControllerRef;
  abortControllerMachineStatistics?: AbortControllerRef;
  abortControllerEventStart?: AbortControllerRef;
  abortControllerEventEnd?: AbortControllerRef;
  abortControllerNcFile?: AbortControllerRef;

  // Timeline data
  startTime: string;
  endTime: string;
  yearIsEnabled: boolean;

  // Functions
  fetchData?: (params?: { start?: string; end?: string }) => Promise<void>;

  // Controls whether effects should depend on selected machine changes
  shouldDependOnSelectedMachine?: boolean;
}

export const useManageTimelineEffects = ({
  abortControllerEvents,
  abortControllerProductionBatches,
  abortControllerAlarmEvent,
  abortControllerOperationsSearch,
  abortControllerMachineStatistics,
  abortControllerEventStart,
  abortControllerEventEnd,
  abortControllerNcFile,
  startTime,
  endTime,
  yearIsEnabled,
  fetchData,
  shouldDependOnSelectedMachine = true,
}: UseManageTimelineEffectsParams) => {
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const { timePeriod: selectedTime } = useHandleParams();
  const { selectedMachine, selectedTimeCounter, updatedMachineEventsTimelineHash } = useTimelineSelectors();

  const prevSelectedTimeRef = useRef<TimePeriod | null>(null);
  const prevSelectedMachineId = useRef<number | null>(null);

  // Cleanup effect for abort controllers
  useEffect(() => {
    return () => {
      abortControllerEvents?.current?.abort();
      abortControllerProductionBatches?.current?.abort();
      abortControllerAlarmEvent?.current?.abort();
      abortControllerOperationsSearch?.current?.abort();
      abortControllerMachineStatistics?.current?.abort();
      abortControllerEventStart?.current?.abort();
      abortControllerEventEnd?.current?.abort();
      abortControllerNcFile?.current?.abort();
    };
  }, shouldDependOnSelectedMachine ? [selectedTime, selectedMachine?.id] : [selectedTime]);

  // Set time period effect
  useEffect(() => {
    if (selectedTime) {
      dispatch(setTimePeriod(selectedTime));
      navigation.setParams({ timePeriod: selectedTime });
    }
  }, [selectedTime]);

  // Reload data on timeline hash change
  useEffect(() => {
    (async () => {
      if (fetchData) {
        await fetchData({});
      }
    })();
  }, [updatedMachineEventsTimelineHash]);

  // Reset when user changes time period or machine
  useEffect(() => {
    (async () => {
      let start = startTime;
      let end = endTime;

      const timePeriod = route.params?.timePeriod;

      const isPrevSelectedTimeExist = !!prevSelectedTimeRef?.current;
      const isPrevAndCurrentTimeTheSame = prevSelectedTimeRef?.current === selectedTime;
      const isPrevSelectedMachineIdExist = !!prevSelectedMachineId?.current;
      const isPrevAndCurrentSelectedMachineIdTheSame = prevSelectedMachineId?.current === selectedMachine?.id;

      const shouldResetOnMachineChange = shouldDependOnSelectedMachine && isPrevSelectedMachineIdExist && !isPrevAndCurrentSelectedMachineIdTheSame;

      if ((isPrevSelectedTimeExist && !isPrevAndCurrentTimeTheSame) || shouldResetOnMachineChange) {
        start = localTime(startTime).startOf(formatStartOf(timePeriod)).format();
        dispatch(setSelectedChartDate(start));
        end = localTime(startTime).add(1, timePeriod).startOf(formatStartOf(timePeriod)).format();

        dispatch(setSelectedChartDateEnd(end));

        if (fetchData) {
          await fetchData({ start, end });
        }
      }

      if (prevSelectedMachineId && shouldDependOnSelectedMachine) {
        prevSelectedMachineId.current = selectedMachine?.id;
      }
    })();
  }, shouldDependOnSelectedMachine ? [selectedMachine?.id, route.params?.timePeriod, selectedTime] : [route.params?.timePeriod, selectedTime]);

  // Reset when user clicks same time range again
  useEffect(() => {
    (async () => {
      if (!!prevSelectedTimeRef?.current && prevSelectedTimeRef.current === selectedTime) {
        const start = localTime(startTime).startOf(formatStartOf(selectedTime)).format();
        dispatch(setSelectedChartDate(start));
        const end = localTime(startTime).clone().add(1, selectedTime).startOf(formatStartOf(selectedTime)).format();

        dispatch(setSelectedChartDateEnd(end));

        if (fetchData) {
          await fetchData({ start, end });
        }
      }

      if (prevSelectedTimeRef) {
        prevSelectedTimeRef.current = selectedTime;
      }
    })();
  }, [selectedTimeCounter, selectedTime]);

  // Focus effect for start time
  useFocusEffect(
    useCallback(() => {
      dispatch(setSelectedChartDate(startTime));
    }, [startTime])
  );

  // Focus effect for end time
  useFocusEffect(
    useCallback(() => {
      dispatch(setSelectedChartDateEnd(endTime));
    }, [endTime])
  );

  // Focus effect for time period handling
  useFocusEffect(
    useCallback(() => {
      const timePeriod = route?.params?.timePeriod;
      const shouldSwitchToMonth = !yearIsEnabled && timePeriod === TimePeriod.YEAR;

      if (timePeriod) {
        if (shouldSwitchToMonth) {
          dispatch(setTimePeriod(TimePeriod.MONTH));
          dispatch(setSelectedChartDate(localTime().startOf('month').format()));
          dispatch(setSelectedChartDateEnd(localTime().add(1, 'month').startOf('month').format()));
        } else {
          dispatch(setTimePeriod(timePeriod));
        }
      }
    }, [route?.params?.timePeriod, yearIsEnabled])
  );

  return { prevSelectedTimeRef, prevSelectedMachineId };
};
